import { useState, useEffect } from 'react';
import type { JourneyItemTemplateCheckInFirestore } from '../../services/journeyTypes';
import { JourneyItemFormFields } from './JourneyItemFormFields';
import { useJourneyItemForm } from './useJourneyItemForm';
import { JourneyItemFormActions } from './JourneyItemFormActions';
import ErrorDisplay from '../../../../components/ErrorDisplay';

interface CheckInSpecificFormData {
    aiPromptGuidance: string;
}

interface CheckInJourneyItemFormProps {
    itemTemplate?: JourneyItemTemplateCheckInFirestore;
    initialOrder: number;
    onSave: (item: JourneyItemTemplateCheckInFirestore) => void;
    onSaveAndContinue?: (item: JourneyItemTemplateCheckInFirestore) => void;
    onCancel: () => void;
}

export const CheckInJourneyItemForm = ({ itemTemplate, initialOrder, onSave, onSaveAndContinue, onCancel }: CheckInJourneyItemFormProps) => {
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue,
    } = useJourneyItemForm({
        itemTemplate,
        initialOrder,
        defaultProgressType: 'fixed',
    });

    const [specificFormData, setSpecificFormData] = useState<CheckInSpecificFormData>({
        aiPromptGuidance: '',
    });

    useEffect(() => {
        if (itemTemplate && itemTemplate.id) {
            setSpecificFormData({
                aiPromptGuidance: itemTemplate.aiPromptGuidance,
            });
        }
    }, [itemTemplate]);

    const handleChange = (field: string, value: string | number | boolean) => {
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value);
        } else {
            setSpecificFormData((prev) => ({ ...prev, [field]: value }));
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.aiPromptGuidance.trim()) {
            return 'AI Prompt Guidance is required';
        }
        return null;
    };

    const createItem = (baseData: Partial<JourneyItemTemplateCheckInFirestore>): JourneyItemTemplateCheckInFirestore => {
        return {
            ...baseData,
            type: 'check-in',
            maxProgress: 1,
            aiPromptGuidance: specificFormData.aiPromptGuidance,
        } as JourneyItemTemplateCheckInFirestore;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createItem, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createItem, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormFields
                formData={commonFormData}
                itemType="check-in"
                disableProgressType={false}
                onChange={handleChange}
            />

            <h2 className="text-lg font-bold mt-2">Check-in-specific fields</h2>
            <hr className="-mt-4" />

            <div>
                <label htmlFor="aiPromptGuidance" className="block text-sm font-medium text-gray-700 mb-1">
                    AI Prompt Guidance *
                </label>
                <textarea
                    id="aiPromptGuidance"
                    value={specificFormData.aiPromptGuidance}
                    onChange={(e) => handleChange('aiPromptGuidance', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter AI prompt guidance"
                    rows={3}
                    required
                />
            </div>

            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!itemTemplate?.id}
            />
        </form>
    );
};
